import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spinner } from '@/components/ui';
import { CloseIcon, PlusIcon, SendIcon } from '@/components/ui/icons';
import { fileIconFor } from '@/components/ui/fileIcons';
import { cn, formatBytes } from '@/lib/utils';

export interface AttachmentPreviewProps {
  /** Files picked but not yet uploaded. An empty list closes the dialog. */
  files: File[];
  /** How many more files this message can still carry. */
  room: number;
  /** Seeds the caption, so a half-typed draft is not lost on picking a file. */
  initialCaption?: string;
  onFilesChange: (files: File[]) => void;
  onCancel: () => void;
  /** Resolves once the upload finishes; false leaves the dialog open to retry. */
  onSend: (caption: string) => Promise<boolean>;
}

const previewable = (type: string) =>
  type.startsWith('image/') || type.startsWith('video/') || type.startsWith('audio/');

/**
 * What a picked file goes through before it is sent: a look at it, a caption,
 * then send — instead of a chip in the composer that says only its name.
 *
 * Nothing here has been uploaded yet. Cancelling costs a round trip to nobody,
 * and the preview is the local file, so it appears the instant it is picked.
 */
export function AttachmentPreview({
  files,
  room,
  initialCaption = '',
  onFilesChange,
  onCancel,
  onSend,
}: AttachmentPreviewProps) {
  const [caption, setCaption] = useState(initialCaption);
  const [active, setActive] = useState(0);
  const [sending, setSending] = useState(false);
  const addRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  /* Object URLs for what can actually be shown. Anything else gets its icon,
     so there is nothing to allocate — or to leak — for a spreadsheet. */
  const previews = useMemo(
    () => files.map((file) => (previewable(file.type) ? URL.createObjectURL(file) : null)),
    [files],
  );
  useEffect(
    () => () => {
      previews.forEach((url) => url && URL.revokeObjectURL(url));
    },
    [previews],
  );

  /* Removing the last file leaves the index past the end. */
  useEffect(() => {
    setActive((index) => Math.min(index, Math.max(0, files.length - 1)));
  }, [files.length]);

  useEffect(() => {
    captionRef.current?.focus();
  }, []);

  const current = files[active];
  const currentUrl = previews[active] ?? null;
  const single = files.length === 1;
  const total = files.reduce((sum, file) => sum + file.size, 0);

  function remove(index: number) {
    const next = files.filter((_, i) => i !== index);
    /* Dropping something earlier in the row shifts everything after it, so the
       preview follows the file it was showing rather than its old slot. */
    if (index < active) setActive((current) => Math.max(0, current - 1));
    onFilesChange(next);
    if (!next.length) onCancel();
  }

  function addFiles(picked: File[]) {
    if (!picked.length) return;
    onFilesChange([...files, ...picked.slice(0, Math.max(0, room))]);
  }

  async function send() {
    if (sending) return;
    setSending(true);
    const sent = await onSend(caption.trim());
    /* A failed upload keeps the dialog — and the caption — exactly as it was. */
    if (!sent) setSending(false);
  }

  if (!current) return null;

  const Icon = fileIconFor(current.type);

  return (
    <Modal
      open
      onClose={() => !sending && onCancel()}
      width="lg"
      title={single ? 'Send file' : `Send ${files.length} files`}
      description={
        single
          ? current.name
          : `${formatBytes(total)} in total — the caption goes out with all of them`
      }
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            disabled={sending}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center justify-center gap-2"
            disabled={sending}
            onClick={() => void send()}
          >
            {sending ? <Spinner className="h-4 w-4" /> : <SendIcon size="sm" />}
            {sending ? 'Sending…' : single ? 'Send' : `Send ${files.length}`}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="relative grid min-h-[12rem] place-items-center overflow-hidden rounded-2xl bg-surface-2 ring-1 ring-surface-3">
          {current.type.startsWith('image/') && currentUrl ? (
            <img
              src={currentUrl}
              alt={current.name}
              className="max-h-[42dvh] w-full object-contain"
            />
          ) : current.type.startsWith('video/') && currentUrl ? (
            <video src={currentUrl} controls playsInline className="max-h-[42dvh] w-full" />
          ) : current.type.startsWith('audio/') && currentUrl ? (
            <div className="w-full p-6">
              <audio src={currentUrl} controls className="w-full" />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 p-8 text-center">
              <span className="grid h-16 w-16 place-items-center rounded-2xl bg-surface-3 text-brand-300">
                <Icon size="lg" />
              </span>
              <p className="max-w-xs truncate text-sm font-medium text-ink">{current.name}</p>
              <p className="text-xs text-slate-500">
                {current.type || 'File'} · {formatBytes(current.size)}
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={() => remove(active)}
            aria-label={`Remove ${current.name}`}
            title="Remove"
            className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
          >
            <CloseIcon size="sm" />
          </button>

          {!single && (
            <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-1 text-[11px] font-medium tabular-nums text-white backdrop-blur-sm">
              {active + 1} / {files.length}
            </span>
          )}
        </div>

        {/* One row of everything going out, so a wrong pick is obvious before
            it is sent rather than after. */}
        {(!single || room > 0) && (
          <ul className="flex gap-2 overflow-x-auto pb-1">
            {files.map((file, index) => {
              const url = previews[index];
              const ThumbIcon = fileIconFor(file.type);
              return (
                <li key={`${file.name}-${file.lastModified}-${index}`} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setActive(index)}
                    aria-label={`Preview ${file.name}`}
                    aria-current={index === active}
                    className={cn(
                      'grid h-16 w-16 place-items-center overflow-hidden rounded-xl bg-surface-2 ring-1 transition-colors',
                      index === active
                        ? 'ring-2 ring-brand-500'
                        : 'ring-surface-3 hover:ring-surface-4',
                    )}
                  >
                    {file.type.startsWith('image/') && url ? (
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-brand-300">
                        <ThumbIcon />
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    aria-label={`Remove ${file.name}`}
                    className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-surface-1 text-slate-300 ring-1 ring-surface-4 transition-colors hover:text-ink"
                  >
                    <CloseIcon size="xs" />
                  </button>
                </li>
              );
            })}
            {room > 0 && (
              <li className="shrink-0">
                <button
                  type="button"
                  onClick={() => addRef.current?.click()}
                  aria-label="Add another file"
                  title="Add another file"
                  className="grid h-16 w-16 place-items-center rounded-xl border border-dashed border-surface-4 text-slate-400 transition-colors hover:border-brand-500 hover:text-brand-300"
                >
                  <PlusIcon />
                </button>
                <input
                  ref={addRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => {
                    addFiles(Array.from(event.target.files ?? []));
                    event.target.value = '';
                  }}
                />
              </li>
            )}
          </ul>
        )}

        <textarea
          ref={captionRef}
          rows={2}
          value={caption}
          disabled={sending}
          onChange={(event) => setCaption(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
          placeholder="Add a caption…"
          aria-label="Caption"
          /* 16px on touch stops iOS Safari zooming the page on focus. */
          className="w-full resize-none rounded-2xl border border-surface-4 bg-surface-2 px-3 py-2.5 text-base text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-brand-500 disabled:opacity-60 sm:text-sm"
        />
      </div>
    </Modal>
  );
}
